import { useEffect, useState, type ComponentType } from 'react'
import { Route, Routes } from 'react-router-dom'
import { routesDef } from '../config/routes'
import Layout from '../components/Layout'
import { PageShell } from '../components/PageShell'
import HomePage from './home'
import DeleteAccountPage from './delete-account'
import JoinSchoolPage from './join-school'
import PrivacyPage from './legal/PrivacyPage'
import TermsPage from './legal/TermsPage'
import NotFoundPage from './NotFound'
import AuthSuccessPage from './auth-success'
import { SupportArea } from './support'

type LazyComponentState<T> = { Component?: T; failed: boolean }

// A useState/useEffect gate rather than React.lazy()+Suspense. This app's SSR pass uses
// ReactDOMServer.renderToString (see entry-server.tsx), which does not support Suspense-boundary
// hydration — verified directly: wrapping a lazy-loaded help route in <Suspense> made
// hydrateRoot throw "The server did not finish this Suspense boundary: The server used
// renderToString which does not support Suspense... switch to renderToPipeableStream" on every
// /help and /help/:slug load. `Component` starts undefined on both the server render and the
// client's first hydration pass, so both emit the identical fallback markup — no mismatch — and
// the effect (client-only; it never runs during SSR) swaps in the real component once the
// browser has fetched its chunk. A rejected import (network failure, or a stale deploy hash
// after a redeploy moves the chunk) sets `failed` instead, so the route can show a terminal
// error state rather than sitting on the loading fallback forever.
const useLazyComponent = <T,>(load: () => Promise<{ default: T }>): LazyComponentState<T> => {
  const [Component, setComponent] = useState<T>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    load()
      .then((mod) => {
        if (!cancelled) setComponent(() => mod.default as T)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
    // Mount-only by design (see the comment above this hook) — `load` is a fresh closure on
    // every render at each call site, so adding it would re-trigger the chunk import every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { Component, failed }
}

// Shown while a help route's own chunk is still loading — a separate, earlier phase from
// HelpListPage/HelpArticlePage's own `loading` (which covers the GraphQL fetch once that chunk
// has already run).
const HelpFallback = () => (
  <PageShell variant="help">
    <p>Loading…</p>
  </PageShell>
)

// Shown when the help chunk itself fails to load — mirrors the "temporarily unavailable"
// treatment HelpListPage/HelpArticlePage use for a failed GraphQL request, so a chunk-load
// failure (dropped network request, or a stale deploy hash after a redeploy moves the chunk)
// reaches the same readable terminal state instead of leaving the route stuck on HelpFallback.
const HelpChunkError = () => (
  <PageShell variant="help">
    <p>Help is temporarily unavailable. Please try again later.</p>
  </PageShell>
)

// Only these three ever load `@apollo/client`/`@ztechapps/common.hook` — every other route
// (including this marketing site's own landing page) loads and runs with zero Apollo overhead.
const HelpLayoutRoute = () => {
  const { Component: HelpApolloProvider, failed } = useLazyComponent(() => import('./help/HelpApolloProvider'))
  if (failed) return <HelpChunkError />
  return HelpApolloProvider ? <HelpApolloProvider /> : <HelpFallback />
}

const HelpListRoute = () => {
  const { Component: HelpListPage, failed } = useLazyComponent<ComponentType>(() => import('./help/HelpListPage'))
  if (failed) return <HelpChunkError />
  return HelpListPage ? <HelpListPage /> : <HelpFallback />
}

const HelpArticleRoute = () => {
  const { Component: HelpArticlePage, failed } = useLazyComponent<ComponentType>(() => import('./help/HelpArticlePage'))
  if (failed) return <HelpChunkError />
  return HelpArticlePage ? <HelpArticlePage /> : <HelpFallback />
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path={routesDef.HomePage.path} element={<HomePage />} />
      </Route>
      {/* Standalone routes render without the marketing header/footer. */}
      <Route path={routesDef.DeleteAccount.path} element={<DeleteAccountPage />} />
      <Route path={routesDef.JoinSchool.path} element={<JoinSchoolPage />} />
      <Route path={routesDef.Privacy.path} element={<PrivacyPage />} />
      <Route path={routesDef.Terms.path} element={<TermsPage />} />
      {/* Lands the sign-in redirect and stores the session — reachable with no session, so it
          stays outside the /support/* mount RequireSupportSession guards. */}
      <Route path={routesDef.AuthSuccess.path} element={<AuthSuccessPage />} />
      {/* The authenticated support area — client-side only, see pages/support/index.tsx. */}
      <Route path="/support/*" element={<SupportArea />} />
      {/* Only these two routes get an Apollo client — see HelpApolloProvider.tsx. Both, and the
          provider itself, load lazily via useLazyComponent above. */}
      <Route element={<HelpLayoutRoute />}>
        <Route path={routesDef.Help.path} element={<HelpListRoute />} />
        <Route path={routesDef.HelpArticle.path} element={<HelpArticleRoute />} />
      </Route>
      <Route path={routesDef.NotFound.path} element={<NotFoundPage />} />
    </Routes>
  )
}
